import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { CATEGORIES, ALLERGEN_LEGEND, imgSrc } from "../../lib/api";

function Pills({ value }) {
  const list = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (!list.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1.5 align-middle">
      {list.map((a) => (
        <span key={a} className="text-[9px] uppercase tracking-[0.14em] text-cream-subtle border border-white/10 rounded-full px-2 py-0.5">
          {a}
        </span>
      ))}
    </span>
  );
}

function ItemRow({ item }) {
  return (
    <div data-testid={`menu-item-${item.id}`} className="group py-3.5 border-b border-white/5 last:border-0">
      <div className="flex items-baseline gap-3">
        {item.image && (
          <img src={imgSrc(item.image)} alt={item.name} loading="lazy" className="w-11 h-11 rounded-lg object-cover border border-white/10 shrink-0 self-center" />
        )}
        <h4 className="font-serif text-base sm:text-lg text-cream group-hover:text-gold-glow transition-colors duration-300 leading-snug">
          {item.name}
        </h4>
        <span className="hidden sm:block flex-1 border-b border-dotted border-cream-subtle/25 -translate-y-1 min-w-6" />
        <span className="ml-auto sm:ml-0 shrink-0 font-num font-semibold text-gold-glow">{item.price}</span>
      </div>
      {item.description && (
        <p className="mt-1 text-sm text-cream-muted font-light leading-relaxed sm:pr-16">{item.description}</p>
      )}
      {item.allergens && <div className="mt-1.5"><Pills value={item.allergens} /></div>}
    </div>
  );
}

export function MenuSection({ items, settings }) {
  const present = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return CATEGORIES.filter((c) => set.has(c.id));
  }, [items]);
  const [active, setActive] = useState(null);
  const current = active && present.some((c) => c.id === active) ? active : present[0]?.id;
  const currentCat = present.find((c) => c.id === current);
  const filtered = items.filter((i) => i.category === current);
  const catIndex = present.findIndex((c) => c.id === current);

  return (
    <section id="menu" className="relative py-24 sm:py-32 bg-slate2 border-y border-white/5" data-testid="menu-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Il Menu</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <SectionTitle>
              Cosa ti va <em className="italic text-gold-glow">stasera?</em>
            </SectionTitle>
            <p className="text-sm text-cream-subtle max-w-sm leading-relaxed">
              Il menu completo di Red Stone Beer and Grill: scegli una categoria per vedere piatti e prezzi.
            </p>
          </div>
        </Reveal>

        {items.length > 0 ? (
          <>
            <div className="sticky top-[72px] sm:top-[76px] z-30 -mx-5 px-5 sm:-mx-8 sm:px-8 mt-10 py-3 bg-slate2/95 backdrop-blur-xl border-y border-white/10" data-testid="menu-category-tabs">
              <div className="flex gap-2 overflow-x-auto no-scrollbar max-w-6xl mx-auto">
                {present.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`menu-tab-${c.id}`}
                    onClick={() => setActive(c.id)}
                    aria-pressed={current === c.id}
                    className={`shrink-0 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] px-4 py-2.5 rounded-full border transition-all duration-300 ${
                      current === c.id
                        ? "bg-burgundy-brand border-gold/40 text-cream"
                        : "border-white/10 text-cream-subtle hover:border-gold/40 hover:text-cream"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-10 min-h-[300px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`menu-category-${current}`}
                >
                  {currentCat && (
                    <>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <span className="font-num text-sm text-gold/60">{String(catIndex + 1).padStart(2, "0")}</span>
                        <h3 className="font-serif text-2xl sm:text-3xl text-cream tracking-tight">{currentCat.label}</h3>
                        {currentCat.allergens && <Pills value={currentCat.allergens} />}
                        <span className="flex-1 hidden sm:block h-px bg-gradient-to-r from-gold/30 to-transparent" />
                      </div>
                      {currentCat.note && (
                        <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-copper">{currentCat.note}</p>
                      )}
                      <div className="mt-4 grid md:grid-cols-2 gap-x-14">
                        {filtered.map((item, idx) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.4) }}
                          >
                            <ItemRow item={item} />
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <Reveal>
              <div className="mt-14 border border-white/10 rounded-2xl p-6 sm:p-8 bg-card2/50">
                <h4 className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold-glow">Elenco degli allergeni</h4>
                <p className="mt-3 text-sm text-cream-muted leading-relaxed">
                  {ALLERGEN_LEGEND.join(" · ")}
                </p>
                <p className="mt-2 text-xs text-cream-subtle italic">
                  Comunicare al personale eventuali allergie e/o intolleranze.
                </p>
              </div>
            </Reveal>
          </>
        ) : (
          <Reveal delay={0.2}>
            <div className="mt-12 border border-dashed border-gold/30 rounded-2xl p-10 sm:p-14 text-center bg-card2/50">
              <p className="font-serif text-2xl text-cream">Il menu completo è in arrivo</p>
              <p className="mt-3 text-sm text-cream-muted max-w-md mx-auto leading-relaxed">
                Stiamo aggiornando la carta. Nel frattempo chiamaci: ti raccontiamo i piatti del giorno.
              </p>
              {settings?.phone && (
                <a
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  data-testid="menu-empty-call-button"
                  className="mt-6 inline-flex items-center gap-2 bg-burgundy-brand hover:bg-burgundy-glow text-cream text-xs font-bold uppercase tracking-[0.2em] px-7 py-3.5 rounded-full border border-gold/30 transition-colors"
                >
                  <Phone className="w-4 h-4" /> {settings.phone}
                </a>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
